import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Highlighter from 'react-highlight-words';
import {
  Breadcrumb,
  Button,
  DatePicker,
  Descriptions,
  Flex,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  HomeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import SEO from './SEO';
import compareTwoValues from '../common/compareTwoValues';
import {
  colourPalette,
  dateFormat,
  filterTimeout,
  modalWidth,
  notificationTimeout,
  notificationType,
  query100,
  requestStatus,
  scrollHeight,
} from '../common/constants';
import debugLog from '../common/debugLog';
import pluralise from '../common/pluralise';
import validateString from '../common/validator';
import { db, primaryAuth } from '../firebase';
import './style.scss';

export default function PendingApproval({
  badgeCount,
  site,
  updateRequestCount,
}) {
  const [buttonStatus, setButtonStatus] = useState(); // use to disable button
  const [data, setData] = useState([]); // request data from database
  const [filterDate, setFilterDate] = useState({ end: '', start: '' }); // date filter
  const [filterOption, setFilterOption] = useState(requestStatus.pendings); // status
  const [isModalOpen, setIsModalOpen] = useState(false); // modal visibility
  const [loading, setLoading] = useState(false); // loading indicator
  const [rowKeyData, setRowKeyData] = useState({}); // row data
  const [rowKeySelected, setRowKeySelected] = useState([]); // row selection
  const [searchColumn, setSearchColumn] = useState('');
  const [searchText, setSearchText] = useState('');

  const [messageApi, contextHolder] = message.useMessage();

  const searchInput = useRef(null);
  const tableParams = {
    position: ['bottomCenter'],
    showSizeChanger: true,
    showTotal: (total, range) =>
      `${range[0]} - ${range[1]} of ${total} ${pluralise(total, 'request')}`,
    total: data.length,
  };

  // destructure imports
  const { RangePicker } = DatePicker;
  const { antdDefault, antdError, antdSuccess, highlighted, searchActive } =
    colourPalette;
  const { normal } = modalWidth;
  const { timeoutError, timeoutInfo, timeoutSuccess, timeoutWarning } =
    notificationTimeout;
  const { typeError, typeInfo, typeSuccess, typeWarning } = notificationType;
  const {
    approved,
    pendings,
    rejected,
    approvedStr,
    pendingsStr,
    rejectedStr,
  } = requestStatus;
  const { id } = site;

  const filterIconColour = (filtered) => (
    <SearchOutlined style={{ color: filtered ? searchActive : undefined }} />
  );

  const filterMenu = (
    clearFilters,
    close,
    confirm,
    dataIndex,
    handleReset,
    handleSearch,
    inputSearch, // searchInput
    selectedKeys,
    // setSearchColumn,
    // setSearchText,
    setSelectedKeys,
  ) => (
    <div className="search-dialog">
      <Input
        id="search-input"
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() =>
          handleSearch(true, confirm, dataIndex, selectedKeys)
        }
        placeholder={`Search ${dataIndex}`}
        ref={inputSearch}
        value={selectedKeys[0]}
      />
      <Space>
        <Button
          className="search-button"
          icon={<SearchOutlined />}
          onClick={() => handleSearch(true, confirm, dataIndex, selectedKeys)}
          size="small"
          type="primary"
        >
          Search
        </Button>
        <Button
          className="search-button"
          onClick={() => handleReset(clearFilters, confirm, dataIndex)}
          size="small"
        >
          Reset
        </Button>
        <Button
          onClick={() => handleSearch(false, confirm, dataIndex, selectedKeys)}
          size="small"
          type="link"
        >
          Filter
        </Button>
        <Button onClick={() => close()} size="small" type="link">
          Close
        </Button>
      </Space>
    </div>
  );

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchRequests = useCallback(
    (dateFilter, optionFilter, siteId, status) => {
      if (validateString(siteId) === false) {
        console.warn('-- Site ID invalid');
        return;
      }

      setLoading(true);
      updateRequestCount('preRegister', siteId, status, {
        altKey: 'pendingApproval',
      });

      const requests = [];
      const requestRef = collection(db, 'preRegister');
      let q = query(
        requestRef,
        where('siteId', '==', siteId),
        where('requestStatus', '==', optionFilter),
      );

      // add date filters if applicable, otherwise add query limit
      if (dateFilter.start !== '' && dateFilter.end !== '') {
        q = query(
          q,
          where('registrationDate', '>=', dateFilter.start),
          where('registrationDate', '<=', dateFilter.end),
        );
      } else {
        q = query(q, limit(query100));
      }

      // order query results
      q = query(q, orderBy('registrationDate', 'desc'));

      getDocs(q)
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            const request = {};
            const dict = doc.data();

            request.blockNumber = dict.blockNumber;
            request.companyName = dict.companyName ?? '--';
            request.contactNumber = dict.contactNumber;
            request.registerID = doc.id;
            request.registration = `${dict.registrationDate} ${dict.registrationTime}`;
            request.requestStatus = dict.requestStatus;
            request.residentName = dict.residentName ?? '--';
            request.unitNumber = dict.unitNumber;
            request.validDates = dict.validDates ?? ['--'];
            request.vehicleNumber = dict.vehicleNumber ?? '--';
            request.visitPurpose = dict.visitPurpose;
            request.visitType = dict.visitType;
            request.visitorName = dict.visitorName;

            requests.push(request);
          });

          if (requests?.length) {
            openMessage(
              `fetching latest ${requests.length} ${pluralise(requests.length, 'request')}`,
              timeoutSuccess,
              typeSuccess,
            );
          } else {
            console.warn('-- No record');
            openMessage('No record', timeoutInfo, typeInfo);
          }

          setData(requests);
          setLoading(false);
        })
        .catch((error) => {
          setData([]);
          setLoading(false);
          console.warn('-- Error getting documents:', error);
          openMessage('Error getting documents', timeoutWarning, typeWarning);
        });
    },
    [
      openMessage,
      timeoutInfo,
      timeoutSuccess,
      timeoutWarning,
      typeInfo,
      typeSuccess,
      typeWarning,
      updateRequestCount,
    ],
  );

  const resetSelection = () => {
    setIsModalOpen(false);
    setRowKeyData({});
    setRowKeySelected([]);
  };

  const handleRequestApprove = () => {
    setLoading(true);

    axios
      .post(
        process.env.REACT_APP_CLOUDRUN_APPROVE_PREREGISTER,
        { registerID: rowKeyData.registerID, uid: primaryAuth.currentUser.uid },
        { headers: { 'Content-Type': 'application/json' } },
      )
      .then((response) => {
        resetSelection();
        setLoading(false);
        fetchRequests(filterDate, filterOption, id, pendings);
        debugLog('-- Request successfully approved', response.status);
        openMessage(
          'Request successfully approved',
          timeoutSuccess,
          typeSuccess,
        );
      })
      .catch((error) => {
        resetSelection();
        setLoading(false);
        fetchRequests(filterDate, filterOption, id, pendings);
        console.error('-- Error:', error.message, error);
        openMessage(`Error: ${error.message}`, timeoutError, typeError);
      });
  };

  const handleRequestReject = () => {
    resetSelection();
    // WIP: must give reason for rejecting the request
  };

  // clear row data and selection then hide the modal
  const handleCancel = () => {
    resetSelection();
    debugLog('-- User cancelled the modal');
  };

  const handleDatePickerChange = (dates) => {
    if (dates === null) {
      setFilterDate({ start: '', end: '' });
    } else {
      const date1 = dayjs(dates[0]).format(dateFormat);
      const date2 = dayjs(dates[1]).format(dateFormat);
      setFilterDate({ start: date1, end: date2 });
    }
  };

  const handleReload = () => {
    fetchRequests(filterDate, filterOption, id, pendings);
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters();
    confirm({ closeDropdown: false }); // dropdown stays open after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(''); // reset search terms
  };

  const handleSearch = (closeDropdown, confirm, dataIndex, selectedKeys) => {
    confirm({ closeDropdown }); // whether dropdown should close after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(selectedKeys[0]); // the search terms
  };

  // format dates to be more readable
  const generateSortedList = (listArr) => {
    if (listArr === undefined || listArr.length <= 1) {
      console.warn('-- Not enough items to generate list');
      return listArr;
    }

    const sortList = listArr.toSorted(); // ascending order
    const itemList = sortList.join(', ');

    return itemList;
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      clearFilters,
      close,
      confirm,
      selectedKeys,
      setSelectedKeys,
    }) =>
      filterMenu(
        clearFilters,
        close,
        confirm,
        dataIndex,
        handleReset,
        handleSearch,
        searchInput,
        selectedKeys,
        // setSearchColumn,
        // setSearchText,
        setSelectedKeys,
      ),
    filterIcon: (filtered) => filterIconColour(filtered),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), filterTimeout);
        }
      },
    },
    render: (text) =>
      searchColumn === dataIndex ? (
        <Highlighter
          autoEscape
          highlightStyle={{ backgroundColor: highlighted, padding: 0 }}
          searchWords={[searchText]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Status',
      dataIndex: 'requestStatus',
      align: 'center',
      width: 120,
      render: (status) => {
        switch (status) {
          case 0:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdDefault}
                icon={<ClockCircleOutlined />}
              >
                {pendingsStr.toUpperCase()}
              </Tag>
            );
          case 1:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdSuccess}
                icon={<CheckOutlined />}
              >
                {approvedStr.toUpperCase()}
              </Tag>
            );
          case 2:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdError}
                icon={<CloseOutlined />}
              >
                {rejectedStr.toUpperCase()}
              </Tag>
            );
          default:
            return <Tag />;
        }
      },
    },
    {
      title: 'Registration',
      dataIndex: 'registration',
      width: 140,
      sortDirections: ['descend', 'ascend'],
      sorter: {
        compare: (a, b) => compareTwoValues(a.registration, b.registration),
        multiple: 1,
      },
    },
    {
      title: 'ID',
      dataIndex: 'registerID',
      width: 260,
      render: (registerID) => <code className="mono">{registerID}</code>,
    },
    {
      title: 'Block',
      dataIndex: 'blockNumber',
      align: 'center',
      width: 100,
      ...getColumnSearchProps('blockNumber'),
    },
    {
      title: 'Unit',
      dataIndex: 'unitNumber',
      align: 'center',
      width: 80,
      ...getColumnSearchProps('unitNumber'),
    },
    {
      title: 'Resident Name',
      dataIndex: 'residentName',
      width: 200,
      ...getColumnSearchProps('residentName'),
    },
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      width: 160,
      ...getColumnSearchProps('companyName'),
    },
    {
      title: 'Visitor Name',
      dataIndex: 'visitorName',
      width: 200,
      ...getColumnSearchProps('visitorName'),
    },
    {
      title: 'Visitor Contact',
      dataIndex: 'contactNumber',
      width: 160,
    },
    {
      title: 'Visitor Vehicle',
      dataIndex: 'vehicleNumber',
      width: 140,
    },
    {
      title: 'Valid On',
      dataIndex: 'validDates',
      width: 120,
      render: (dates) => dates.toSorted().join(', '),
    },
    // {
    //   vehicle number, visitpurpose, visittype
    // },
  ];

  const requestItems = (rowData) => [
    {
      key: 'residentName',
      label: 'Requested By',
      children: rowData.residentName,
    },
    {
      key: 'blockUnit',
      label: 'Unit',
      children: `Block ${rowData.blockNumber} #${rowData.unitNumber}`,
    },
    {
      key: 'visitorName',
      label: 'Visitor Name',
      children: rowData.visitorName,
    },
    {
      key: 'companyName',
      label: 'Company Name',
      children: rowData.companyName,
    },
    {
      key: 'contactNumber',
      label: 'Visitor Contact',
      children: rowData.contactNumber,
    },
    {
      key: 'vehicleNumber',
      label: 'Visitor Vehicle',
      children: rowData.vehicleNumber,
    },
    {
      key: 'dateTime',
      label: 'Requested On',
      span: 'filled',
      children: rowData.registration,
    },
    {
      key: 'validDates',
      label: 'Requested Dates',
      span: 'filled',
      children: generateSortedList(rowData.validDates),
    },
  ];

  useEffect(() => {
    fetchRequests(filterDate, filterOption, id, pendings);
  }, [fetchRequests, filterDate, filterOption, id, pendings]);

  return (
    <>
      <SEO
        description="Preregistered requests that require management approval"
        name="qiwei"
        title={`(${badgeCount}) Pending Approval`}
        type="webapp"
      />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
            {
              title: <span>Pending Approval</span>,
            },
          ]}
        />
        <Flex align="center" wrap>
          <RangePicker
            format={dateFormat}
            id="rangePicker"
            name="rangePicker"
            onChange={handleDatePickerChange}
            showNow
            variant="borderless"
          />
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={handleReload}
            type="link"
          >
            Reload
          </Button>
          <Radio.Group
            buttonStyle="solid"
            defaultValue={pendings}
            id="radioGroup"
            name="statusFilter"
            onChange={(e) => setFilterOption(e.target.value)}
          >
            <Radio.Button value={pendings}>
              {pendingsStr.toUpperCase()}
            </Radio.Button>
            <Radio.Button value={approved}>
              {approvedStr.toUpperCase()}
            </Radio.Button>
            <Radio.Button value={rejected}>
              {rejectedStr.toUpperCase()}
            </Radio.Button>
          </Radio.Group>
        </Flex>
      </div>
      {contextHolder}
      <Table
        className="table margin"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={tableParams}
        rowKey="registration"
        rowSelection={{
          align: 'center',
          columnWidth: 40,
          onChange: (selectedRowKeys, selectedRows) => {
            // console.log(selectedRowKeys, selectedRows[0]);
            setButtonStatus(selectedRows[0].requestStatus);
            setIsModalOpen(true);
            setRowKeyData(selectedRows[0]);
            setRowKeySelected(selectedRowKeys);
          },
          selectedRowKeys: rowKeySelected,
          type: 'radio',
        }}
        scroll={{ x: 1580, y: scrollHeight }}
      />
      {Object.keys(rowKeyData ?? {}).length > 0 && (
        <Modal
          footer={[
            <Button key="cancel" onClick={handleCancel}>
              Cancel
            </Button>,
            <Button
              danger
              disabled={buttonStatus !== pendings} // only enabled for pending
              icon={<CloseOutlined />}
              key="reject"
              onClick={handleRequestReject}
              type="default"
            >
              Reject
            </Button>,
            <Button
              disabled={buttonStatus !== pendings}
              icon={<CheckOutlined />}
              key="approve"
              loading={loading}
              onClick={handleRequestApprove}
              type="primary"
            >
              Approve
            </Button>,
          ]}
          onCancel={handleCancel}
          open={isModalOpen}
          title={`Approval Request [ID: ${rowKeyData.registerID}]`}
          width={normal}
        >
          <Descriptions
            bordered={false}
            colon
            column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
            items={requestItems(rowKeyData)}
            layout="horizontal"
            title={' '}
          />
        </Modal>
      )}
    </>
  );
}

PendingApproval.propTypes = {
  badgeCount: PropTypes.number.isRequired,
  site: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  updateRequestCount: PropTypes.func.isRequired,
};
